
import { toast } from "sonner";
import { Json } from "@/integrations/supabase/types";
import { FormData } from "./types";
import { sendNotification } from "@/utils/sendNotification";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";

export function useCreateUser(fetchUsers: () => void) {
  const [isCreating, setIsCreating] = useState(false);

  const createUser = async (formData: FormData, onFinish?: () => void) => {
    try {
      // Validate required fields
      if (!formData.name) {
        toast.error("Name is required");
        return;
      }

      if (!formData.email) {
        toast.error("Email is required");
        return;
      }

      if (!formData.password) {
        toast.error("Temporary password is required");
        return;
      }

      // Get the current session token
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token;

      if (!authToken) {
        toast.error("You must be logged in to create a user");
        return;
      }

      setIsCreating(true);
      toast.info("Creating user...");
      
      // Format request data
      const requestData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: formData.role,
        verified: formData.verified ?? false,
        country_code: formData.country_code || null,
        constituency_code: formData.constituency_code || null,
        phone_number: formData.phone_number ?? null,
        demographics: (formData.demographics ?? null) as unknown as Json,
      };
      
      console.log("Creating user with data:", {
        ...requestData,
        password: "[REDACTED]" // Don't log the actual password
      });
      
      try {
        // Call the edge function
        const response = await fetch(
          "https://buigjjovjqckdwlazhlx.functions.supabase.co/createUserWithCredentials",
          {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${authToken}`
            },
            body: JSON.stringify(requestData),
          }
        );

        // Parse the response
        const responseData = await response.json();
        
        if (!response.ok) {
          console.error("User creation error:", responseData);
          
          // Handle specific error cases
          if (responseData.error?.includes("already exists")) {
            toast.error(`Email ${formData.email} already exists. Try using a different email.`);
            return;
          }
          
          toast.error(`Failed to create user: ${responseData.error || 'Unknown error'}`);
          return;
        }
        
        // Check for warnings even in successful responses
        if (responseData.warning) {
          toast.warning(`${responseData.message} (${responseData.warning})`);
          console.warn("User created with warnings:", responseData.warning);
        } else {
          toast.success(`User created successfully!`);
        }
        
        // Always show credentials
        toast.info(
          `Temporary credentials:\nEmail: ${formData.email}\nPassword: ${formData.password}`
        );
        
        // Refresh the users list
        fetchUsers();

        // Try to send welcome email w/ credentials after admin creation
        // But don't fail if it doesn't work - make it non-blocking
        try {
          await sendNotification("authSignup", formData.email, {
            recipientName: formData.name,
            credentials: { email: formData.email, password: formData.password },
          }).catch(notificationError => {
            // Log but ignore notification errors to not block user creation
            console.error("Failed to send welcome notification:", notificationError);
          });
        } catch (notificationError) {
          console.error("Failed to send welcome notification:", notificationError);
          // Don't fail if notification sending fails
        }

        if (onFinish) onFinish();
      } catch (fetchError) {
        console.error("Error calling createUserWithCredentials:", fetchError);
        toast.error("Network error when creating user. Please try again.");
      }
    } catch (error: any) {
      console.error("User creation exception:", error);
      toast.error(error.message || "Failed to create user");
    } finally {
      setIsCreating(false);
    }
  };

  return { createUser, isCreating };
}
